import {
  ICellRendererParams,
  ColDef,
  ValueGetterParams,
  ColGroupDef
} from "ag-grid-enterprise";
import {
  numberCellEditorParams,
  numberFilterParams
} from "components/cell-editor/numberCellEditor";

import {
  ActionsCellRenderer,
  bidPackageDueDateHeaderComponent,
  CellValueRendererWithEditIcon,
  dateCellRenderer,
  dueDateCellRenderer,
  missingInSyncCellRenderer,
  nextDeadlineCellRenderer,
  riskCellRenderer,
  specSectionLabel
} from "components/cell-renders";
import {
  twoRowGetQuickFilterText,
  twoRowCellRenderer,
  ArrayStringGetQuickFilterText,
  submittalStatusCellRendererV2,
  FeatureMaterialLinksCellRenderer,
  reviewResponsesCellRenderer
} from "entity-app/shared-components/log-render-components/cell-renders";

import {
  coldefNavigation,
  coldefSingleField,
  coldefHiddenField,
  coldefDateField,
  coldefLinkedActivities,
  coldefUser,
  dateTimeAuditColumnOverrides
} from "entity-app/shared-components/log-render-components/col-defs";
import { TBidPackageLog } from "pages/preconstruction-log-page/model";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import featureDateBlockColumns, {
  generateSavedColConfigsForColIds,
  getInterleavedDateBlockColumnIds,
  isMilestoneOrDurationColumnId
} from "entity-app/shared-components/entity-milestones-duration-columns/feature-date-block-columns";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { UtilColumnConfig } from "components/column-config-views/column-config-util";
import {
  MasterColumnConfigRow,
  resolveEffectiveColumnConfig
} from "components/column-config-views/master-column-config";
import { QUERY_MASTER_COLUMN_CONFIG } from "components/column-config-views/master-column-config-queries";
import { MUTATION_SAVE_COLUMNS } from "services/graphQL/mutations";
import { useCIQMutation, useCIQQuery } from "hooks/ciq-gql-hooks";
import { getLoggedinUserId } from "services/auth";
import { debounce, upperCase } from "lodash";
import { DateFilter, DateUtils } from "utils/dateutils";
import { FeatureSpecSectionCellEditor } from "components/cell-editor/spec-section-editor";
import SelectCellEditor, {
  SelectCellEditorV2
} from "components/cell-editor/select-cell-editor";
import { submittalSequenceComparator } from "pages/submittals-log-page/utils";
import { FeatureTypes } from "entity-app/constants";
import CustomDateSetFilter from "entity-app/custom-filters/custom-date-filter";
import CustomDateCellEditor from "components/cell-editor/date-cell-editor";
import {
  backwardCompatibilityForLinkedTasksFilter,
  comparatorWithEmptyAtBottom,
  riskLevelComparator,
  linkedMaterialsComparator,
  linkedTasksComparator,
  notFoundInSourceComparator,
  reviewResponsesComparator
} from "utils/grid-utils";
import { buildCustomFieldColumnDefs } from "components/custom-fields/log-page";
import { useEnumValues } from "hooks/enum-values";
import { isProjectInIntegrationMode } from "utils/utils";
import { RiskLevelType } from "components/date-block/models";
import {
  ESubmittalStatus,
  ProjectPermissionEnum,
  SubmittalStatusToStrMap
} from "../../../constants";
import { QUERY_GET_SUBMITTAL_COLUMN_CONFIG } from "../submittal-queries";

function useSubmittalLogGridConfig(
  projectId: string,
  gridRef: React.RefObject<any>,
  maxNumberOfMilestones: {
    milestonesCount: number;
    lastMilestoneHasOffset: boolean;
    milestoneIndicesWithOffset: Set<number>;
  } | null,
  options?: {
    getConditionalsFormatting?: () => Array<any>;
    onConditionalsFormattingChange?: (rules: Array<any>) => void;
    skipAuxiliaryQueries?: boolean;
  }
) {
  const { getConditionalsFormatting, onConditionalsFormattingChange } =
    options ?? {};
  const skipAuxiliaryQueries = options?.skipAuxiliaryQueries ?? false;
  const {
    gqlClientForProject,
    columnHeaders: { submittalHeaderMap, submittalCustomColumns },
    projectDetails,
    tokenContents,
    isProcoreProject,
    isProjectArchived
  } = useContext(ProjectContext);

  const { enumOptionsMap } = useEnumValues(
    FeatureTypes.SUBMITTALS,
    skipAuxiliaryQueries
  );

  const isIntegrationMode = useMemo(
    () => isProjectInIntegrationMode(projectDetails?.mode || 0),
    [projectDetails?.mode]
  );

  const [saveColumnsStateMutation] = useCIQMutation(MUTATION_SAVE_COLUMNS, {
    client: gqlClientForProject
  });

  const loggedinUserId = useMemo(() => getLoggedinUserId(), []);
  const {
    data: columnConfigData,
    loading: userColumnConfigLoading,
    refetch: refetchUserColumnConfig
  } = useCIQQuery<{ project_users: Array<any> }>(
    QUERY_GET_SUBMITTAL_COLUMN_CONFIG,
    {
      fetchPolicy: "no-cache",
      client: gqlClientForProject,
      skip: skipAuxiliaryQueries || !gqlClientForProject || !loggedinUserId,
      variables: {
        where: { user_id: { _eq: loggedinUserId } }
      }
    }
  );

  const { data: masterColumnConfigData, loading: masterColumnConfigLoading } =
    useCIQQuery<{ master_column_config: MasterColumnConfigRow[] }>(
      QUERY_MASTER_COLUMN_CONFIG,
      {
        fetchPolicy: "cache-first",
        client: gqlClientForProject,
        skip: skipAuxiliaryQueries || !gqlClientForProject || !projectId,
        variables: {
          where: { project_id: { _eq: projectId } }
        }
      }
    );

  const isColumnConfigReady = skipAuxiliaryQueries
    ? true
    : !userColumnConfigLoading && !masterColumnConfigLoading;

  const isInlineEditSubmittalPermissionGranted = useMemo(() => {
    if (!tokenContents?.role) return false;
    return !isPermissionNotGrantted(
      ProjectPermissionEnum.InlineEditSubmittal,
      tokenContents?.role
    );
  }, [tokenContents?.role]);

  const isVoidSubmittal = useCallback((params: any) => {
    const submittalWFStatus = upperCase(
      params?.data?.workflow_status?.code || ""
    );
    return submittalWFStatus === ESubmittalStatus[ESubmittalStatus.VOID];
  }, []);

  const isSubmittalWFNotStarted = useCallback((params: any) => {
    const submittalWFStatus = upperCase(
      params?.data?.workflow_status?.code || ""
    );
    return submittalWFStatus === ESubmittalStatus[ESubmittalStatus.CREATE];
  }, []);

  const isSubmittalWFCompleted = useCallback((params: any) => {
    const submittalWFStatus = upperCase(
      params?.data?.workflow_status?.code || ""
    );
    return submittalWFStatus === ESubmittalStatus[ESubmittalStatus.DONE];
  }, []);

  const isGridCellEditableForBasicFields = useCallback(
    (params: any) => {
      const isDeletedSub = params?.data?.deleted_from_source;
      return (
        isInlineEditSubmittalPermissionGranted &&
        !isVoidSubmittal(params) &&
        !params.context.isIntegrationMode &&
        !isDeletedSub
      );
    },
    [isInlineEditSubmittalPermissionGranted, isVoidSubmittal]
  );

  const isGridCellEditableForWorkflowFields = useCallback(
    (params: any) => {
      const ignoreIntegrationModeColIds = ["planned_float"];
      const shouldIgnoreIntegrationMode = ignoreIntegrationModeColIds.includes(
        params?.colDef?.colId
      );
      const isDeletedSub = params?.data?.deleted_from_source;

      return (
        isInlineEditSubmittalPermissionGranted &&
        isSubmittalWFNotStarted(params) &&
        !isVoidSubmittal(params) &&
        (shouldIgnoreIntegrationMode || !params.context.isIntegrationMode) &&
        !isDeletedSub
      );
    },
    [
      isInlineEditSubmittalPermissionGranted,
      isVoidSubmittal,
      isSubmittalWFNotStarted
    ]
  );

  const isGridCellEditableForWorkflowFieldsNotClosed = useCallback(
    (params: any) => {
      const ignoreIntegrationModeColIds = [
        "planned_float",
        "manual_entry_date"
      ];
      const shouldIgnoreIntegrationMode = ignoreIntegrationModeColIds.includes(
        params?.colDef?.colId
      );
      const isDeletedSub = params?.data?.deleted_from_source;

      return (
        isInlineEditSubmittalPermissionGranted &&
        !isSubmittalWFCompleted(params) &&
        !isVoidSubmittal(params) &&
        (shouldIgnoreIntegrationMode || !params.context.isIntegrationMode) &&
        !isDeletedSub
      );
    },
    [
      isInlineEditSubmittalPermissionGranted,
      isVoidSubmittal,
      isSubmittalWFCompleted
    ]
  );

  const isEnumFieldEditable = useCallback(
    (params: any) => {
      return !isProjectArchived && !isVoidSubmittal(params);
    },
    [isVoidSubmittal, isProjectArchived]
  );

  const coldefCustomFields = useMemo(() => {
    return buildCustomFieldColumnDefs(
      submittalCustomColumns,
      enumOptionsMap,
      isEnumFieldEditable
    );
  }, [submittalCustomColumns, enumOptionsMap, isEnumFieldEditable]);

  const dateBlockColumnsDefs = useMemo(() => {
    const milestonesCount = maxNumberOfMilestones?.milestonesCount ?? 0;
    if (milestonesCount <= 0) {
      return {
        groupCols: [],
        columnDefsForDateBlock: [],
        dateBlockColumnIds: { milestones: [], durations: [] }
      } as ReturnType<typeof featureDateBlockColumns>;
    }

    return featureDateBlockColumns(
      milestonesCount,
      maxNumberOfMilestones?.lastMilestoneHasOffset ?? false,
      maxNumberOfMilestones?.milestoneIndicesWithOffset
    );
  }, [maxNumberOfMilestones]);

  const onEditCell = useCallback(
    (params: any) => {
      gridRef?.current?.api?.setFocusedCell(params.rowIndex, params.colKey);
      gridRef?.current?.api?.startEditingCell({
        rowIndex: params.rowIndex,
        colKey: params.colKey
      });
    },
    [gridRef]
  );

  const gridDefaultColDef = useMemo(
    () =>
      ({
        sortable: true,
        editable: false,
        filter: true,
        resizable: true,
        minWidth: 100,
        cellRenderer: (params: ICellRendererParams) => {
          return CellValueRendererWithEditIcon(params, false);
        },
        cellEditorPopup: false,
        comparator: comparatorWithEmptyAtBottom
      }) as ColDef<TBidPackageLog>,
    []
  );

  const gridColumnDefs = useMemo(() => {
    return [
      coldefNavigation(
        {
          field: "sequence_id",
          colId: "submittal_sequence_id",
          headerName: submittalHeaderMap.submittal_id,
          pinned: "left",
          lockPosition: "left",
          initialWidth: 80,
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams,
          sortable: true,
          editable: false,
          valueGetter: (params: ValueGetterParams) => {
            const { context, data } = params;
            if (!data.sequence_id) return "";
            // sequence_id is guaranteed truthy by the guard above, so the `|| ""` fallback is unreachable
            /* v8 ignore next */
            const value = data.sequence_id || "";
            const cellText =
              context.projectDetails?.spec_section &&
              data.spec_section.length > 0
                ? `${data.spec_section[0].number} - ${data?.sequence_id}`
                : `${value}`;
            return cellText;
          },
          comparator: (valueA: any, valueB: any, nodeA: any, nodeB: any) =>
            submittalSequenceComparator(
              nodeA.data.sequence_id,
              nodeB.data.sequence_id,
              nodeA,
              nodeB,
              projectDetails?.spec_section
            )
        },
        projectId,
        "submittals"
      ),
      coldefSingleField({
        field: "number",
        colId: "submittal_number",
        headerName: submittalHeaderMap?.submittal_number,
        pinned: "left",
        lockPosition: "left",
        initialWidth: 120,

        editable: isGridCellEditableForBasicFields,
        cellRenderer: CellValueRendererWithEditIcon,
        cellClass: (params: any) => {
          return isGridCellEditableForBasicFields(params)
            ? "cell-editable"
            : "";
        }
      }),
      coldefSingleField({
        field: "title",
        colId: "title",
        headerName: submittalHeaderMap.title,
        filter: true,
        sortable: true,
        initialWidth: 180,
        pinned: "left",
        lockPosition: "left",
        cellRenderer: twoRowCellRenderer,
        cellRendererParams: {
          featureKey: "submittals",
          projectId
        },
        getQuickFilterText: twoRowGetQuickFilterText,
        lockVisible: true,
        cellClass: (params: any) => {
          return isGridCellEditableForBasicFields(params)
            ? "cell-editable"
            : "";
        },
        comparator(valueA: any, valueB: any, nodeA: any, nodeB: any) {
          const compareA = nodeA?.data?.title ?? nodeA?.data?.description;
          const compareB = nodeB?.data?.title ?? nodeB?.data?.description;
          return compareA?.toLowerCase().localeCompare(compareB?.toLowerCase());
        },
        editable: isGridCellEditableForBasicFields
      }),
      coldefHiddenField({
        field: "description",
        headerName: "Description",
        pinned: "left",
        lockPosition: "left"
      }),
      coldefSingleField({
        field: "workflow_status",
        colId: "status",
        headerName: submittalHeaderMap?.workflow_status,
        lockVisible: true,
        initialWidth: 200,
        minWidth: 200,
        tooltipField: undefined,
        tooltipValueGetter: undefined,
        cellRenderer: submittalStatusCellRendererV2,
        filter: "agSetColumnFilter",
        valueGetter: (params: ValueGetterParams) => {
          return SubmittalStatusToStrMap[params.data.workflow_status.id];
        }
      }),
      coldefSingleField({
        field: "procurement_ready",
        colId: "procurement_ready",
        headerName:
          submittalHeaderMap?.procurement_ready || "Procurement Readiness",
        headerTooltip:
          submittalHeaderMap?.procurement_ready || "Procurement Readiness",
        initialWidth: 180,
        editable: false,
        valueGetter: (params: ValueGetterParams) => {
          return params.data?.procurement_ready ? "Ready" : "Not Ready";
        }
      }),
      coldefDateField({
        field: "planned_next_milestone_date",
        colId: "planned_next_milestone_date",
        headerName: submittalHeaderMap?.planned_next_milestone_date,
        initialWidth: 160,
        valueGetter: (params: ValueGetterParams) => {
          return DateUtils.format(
            params.data.date_block?.planned_next_milestone_date
          );
        },
        cellRenderer: (params: ICellRendererParams) => {
          return nextDeadlineCellRenderer(params, false);
        }
      }),
      coldefDateField({
        field: "projected_next_milestone_date",
        colId: "projected_next_milestone_date",
        headerName: submittalHeaderMap?.projected_next_milestone_date,
        initialWidth: 160,
        valueGetter: (params: ValueGetterParams) => {
          return DateUtils.format(
            params.data.date_block?.projected_next_milestone_date
          );
        },
        cellRenderer: (params: ICellRendererParams) => {
          return nextDeadlineCellRenderer(params, false);
        }
      }),
      coldefDateField({
        field: "next_planned_deadline",
        colId: "next_planned_deadline",
        headerName: submittalHeaderMap?.next_planned_deadline || "",
        headerTooltip: submittalHeaderMap?.next_planned_deadline || "",
        cellRenderer: (params: ICellRendererParams) => {
          return nextDeadlineCellRenderer(params, false);
        }
      }),
      coldefSingleField({
        field: "next_action",
        colId: "next_action",
        headerName: submittalHeaderMap?.next_action || "",
        initialWidth: 180,
        editable: false,
        filter: false
      }),
      coldefDateField({
        colId: "manual_entry_date",
        field: "manual_entry_date",
        headerName: submittalHeaderMap?.manual_entry_date || "Deadline Date",
        editable: true,
        cellRenderer: CellValueRendererWithEditIcon,
        cellEditor: CustomDateCellEditor,
        valueGetter: undefined,
        valueFormatter: ({ value }) => (value ? DateUtils.format(value) : ""),
        cellEditorParams: {
          placeholder: `Select ${
            submittalHeaderMap?.manual_entry_date || "Deadline Date"
          }`
        },
        cellClass: (params: any) => {
          return isGridCellEditableForWorkflowFieldsNotClosed(params)
            ? "cell-editable"
            : "";
        }
      }),
      coldefDateField({
        field: "date_block.final_deadline_date",
        colId: "final_deadline_date",
        headerName: submittalHeaderMap?.final_deadline_date || "Deadline",
        headerTooltip: submittalHeaderMap?.final_deadline_date || "Deadline",
        valueGetter: (params: ValueGetterParams) => {
          return DateUtils.format(params.data.date_block?.final_deadline_date);
        },
        cellRenderer: (params: ICellRendererParams) => {
          return nextDeadlineCellRenderer(params, false);
        }
      }),
      coldefDateField({
        field: "date_block.projected_last_milestone_date",
        colId: "projected_last_milestone_date",
        headerName:
          submittalHeaderMap?.projected_last_milestone_date ||
          "Projected Completion Date",
        headerTooltip:
          submittalHeaderMap?.projected_last_milestone_date ||
          "Projected Completion Date",
        valueGetter: (params: ValueGetterParams) => {
          return DateUtils.format(
            params.data.date_block?.projected_last_milestone_date
          );
        },
        cellRenderer: (params: ICellRendererParams) => {
          return nextDeadlineCellRenderer(params, false);
        }
      }),
      coldefDateField({
        field: "due_date",
        colId: "due_date",
        headerName: submittalHeaderMap?.due_date || "",
        headerTooltip: submittalHeaderMap?.due_date || "",
        cellRenderer: (params: ICellRendererParams) => {
          return dueDateCellRenderer(params, false);
        },
        headerComponentParams: bidPackageDueDateHeaderComponent
      }),
      coldefSingleField({
        field: "planned_float",
        colId: "planned_float",
        headerName: "Submittal Planned Float",
        headerTooltip: "Submittal Planned Float",
        initialWidth: 130,
        editable: isGridCellEditableForWorkflowFields,
        cellEditor: "agNumberCellEditor",
        cellEditorParams: numberCellEditorParams,
        filter: "agNumberColumnFilter",
        filterParams: numberFilterParams,
        cellClass: (params: any) => {
          return isGridCellEditableForWorkflowFields(params)
            ? "cell-editable"
            : "";
        }
      }),
      coldefSingleField({
        field: "effective_float",
        colId: "effective_float",
        headerName: "Submittal Actual Float",
        headerTooltip: "Submittal Actual Float",
        initialWidth: 130,
        filter: "agNumberColumnFilter",
        filterParams: numberFilterParams,
        editable: false
      }),
      coldefSingleField({
        field: "spec_section",
        colId: "custom_spec_section",
        headerName: submittalHeaderMap?.spec_no,
        headerTooltip: submittalHeaderMap?.spec_no,
        getQuickFilterText: ArrayStringGetQuickFilterText,
        valueGetter: ({ data }) => {
          if (!data?.spec_section?.length) return "";
          const spec = data.spec_section[0];
          const specLabel = specSectionLabel({
            specNumber: spec?.number,
            specName: spec?.name
          });

          return specLabel;
        },
        tooltipField: undefined,
        editable: isGridCellEditableForBasicFields,
        cellEditor: FeatureSpecSectionCellEditor,
        initialWidth: 300,
        minWidth: 300,
        cellClass: (params: any) => {
          return isGridCellEditableForBasicFields(params)
            ? "cell-editable"
            : "";
        }
      }),
      coldefSingleField({
        field: "revision",
        colId: "revision",
        headerName: submittalHeaderMap?.revision || "",
        headerTooltip: submittalHeaderMap?.revision || "",
        tooltipField: undefined,
        editable: false,
        initialWidth: 150,
        minWidth: 100,
        lockVisible: true
      }),
      coldefSingleField({
        field: "type.value",
        colId: "type",
        headerName: submittalHeaderMap?.type || "",
        headerTooltip: submittalHeaderMap?.type || "",
        tooltipField: undefined,
        editable: isGridCellEditableForBasicFields,
        initialWidth: 150,
        minWidth: 100,
        cellEditor: SelectCellEditorV2,
        lockVisible: true,
        cellEditorParams: {
          type: "regular-select",
          dataMapVar: "typesMap",
          valueVar: "id",
          labelVar: "value",
          showSearch: true
        },
        cellClass: (params: any) => {
          return isGridCellEditableForBasicFields(params)
            ? "cell-editable"
            : "";
        }
      }),
      coldefSingleField({
        field: "state.code",
        colId: "state",
        headerName: submittalHeaderMap?.state || "",
        headerTooltip: submittalHeaderMap?.state || "",
        tooltipField: undefined,
        editable: false,
        filter: "agSetColumnFilter",
        initialWidth: 150,
        minWidth: 100
      }),
      coldefUser({
        def: {
          field: "assignee_user",
          colId: "assignee",
          headerName: submittalHeaderMap?.assignee,
          editable: false,
          initialWidth: 180
        },
        user: {
          id: "assignee_user.id",
          firstName: "assignee_user.first_name",
          lastName: "assignee_user.last_name",
          statusId: "assignee_user.status_id",
          companyObjField: "assignee_user.company"
        }
      }),

      coldefUser({
        def: {
          field: "gc_user",
          colId: "gc_reviewer",
          headerName: submittalHeaderMap?.gc_reviewer,
          editable: isGridCellEditableForWorkflowFields,
          initialWidth: 180,
          cellClass: (params: any) => {
            return isGridCellEditableForWorkflowFields(params)
              ? "cell-editable"
              : "";
          }
        },
        user: {
          id: "gc_user.id",
          firstName: "gc_user.first_name",
          lastName: "gc_user.last_name",
          statusId: "gc_user.status_id",
          companyObjField: "gc_user.company"
        }
      }),

      coldefUser({
        def: {
          field: "design_reviewer",
          colId: "design_reviewer",
          headerName: submittalHeaderMap?.design_reviewer,
          editable: isGridCellEditableForWorkflowFields,
          initialWidth: 180,
          cellClass: (params: any) => {
            return isGridCellEditableForWorkflowFields(params)
              ? "cell-editable"
              : "";
          }
        },
        user: {
          id: "design_reviewer.id",
          firstName: "design_reviewer.first_name",
          lastName: "design_reviewer.last_name",
          statusId: "design_reviewer.status_id",
          companyObjField: "design_reviewer.company",
          dropdownType: "DR_dropdown"
        }
      }),
      coldefUser({
        def: {
          field: "responsible_contractor",
          colId: "responsible_contractor",
          headerName: submittalHeaderMap?.responsible_contractor,
          editable: isGridCellEditableForWorkflowFields,
          initialWidth: 220,
          cellClass: (params: any) => {
            return isGridCellEditableForWorkflowFields(params)
              ? "cell-editable"
              : "";
          }
        },
        user: {
          id: "responsible_contractor.id",
          firstName: "responsible_contractor.name",
          lastName: "",
          dropdownType: "RC_dropdown",
          participantsSource: "responsibleContractors"
        }
      }),

      coldefUser({
        def: {
          field: "submitter_reviewer",
          colId: "submitter",
          headerName: submittalHeaderMap?.submitter,
          editable: isGridCellEditableForWorkflowFields,
          initialWidth: 180,
          cellClass: (params: any) => {
            return isGridCellEditableForWorkflowFields(params)
              ? "cell-editable"
              : "";
          }
        },
        user: {
          id: "submitter_reviewer.id",
          firstName: "submitter_reviewer.first_name",
          lastName: "submitter_reviewer.last_name",
          statusId: "submitter_reviewer.status_id",
          companyObjField: "submitter_reviewer.company",
          dependentId: "responsible_contractor.id",
          dependentNotFoundContent: "First select a Responsible Contractor",
          dropdownType: "RC_ASSIGNEE_dropdown"
        }
      }),
      coldefSingleField({
        field: "review_response",
        colId: "review_response",
        headerName: submittalHeaderMap?.review_response || "Review Response",
        headerTooltip: submittalHeaderMap?.review_response || "Review Response",
        initialWidth: 200,
        editable: false,
        cellRenderer: reviewResponsesCellRenderer,
        filter: true,
        sortable: true,
        cellClass: "excelWrapText",
        comparator: reviewResponsesComparator,
        filterValueGetter: (params: ValueGetterParams) => {
          const reviewResponse = params.data?.review_response || [];
          return reviewResponse.flatMap((r: any) => r?.response);
        },
        valueGetter: (params: ValueGetterParams) => {
          const reviewResponse = params.data?.review_response || [];
          return reviewResponse;
        }
      }),
      coldefSingleField({
        field: "final_response",
        colId: "final_response",
        headerName: submittalHeaderMap?.final_response || "Final Response",
        headerTooltip: submittalHeaderMap?.final_response || "Final Response",
        cellRenderer: reviewResponsesCellRenderer,
        filter: true,
        valueGetter: (params: ValueGetterParams) => {
          const finalResponse = params.data?.final_response || [];
          return finalResponse;
        },
        filterValueGetter: (params: ValueGetterParams) => {
          const finalResponse = params.data?.final_response || [];
          return finalResponse.flatMap((r: any) => r?.response);
        },
        cellClass: "excelWrapText",
        initialWidth: 160,
        editable: false,
        sortable: true,
        comparator: reviewResponsesComparator
      }),
      coldefLinkedActivities({
        field: "tasks",
        colId: "linked_tasks",
        cellDataType: false,
        headerName: submittalHeaderMap?.linked_activities || "",
        headerTooltip: submittalHeaderMap?.linked_activities || "",
        cellClass: "excelWrapText",
        comparator: linkedTasksComparator
      }),
      coldefSingleField({
        field: "linked_items",
        colId: "linked_materials",
        headerName: submittalHeaderMap?.linked_materials || "",
        headerTooltip: submittalHeaderMap?.linked_materials || "",
        cellClass: "excelWrapText",
        initialWidth: 400,
        minWidth: 400,
        cellRenderer: FeatureMaterialLinksCellRenderer,
        valueGetter: ({ data }: any) => {
          return data?.linked_items
            ?.filter(
              (item: any) =>
                item.feature_type === FeatureTypes.MATERIALS && !item.implicit
            )
            ?.map((material: any) => {
              return material;
            });
        },
        comparator: linkedMaterialsComparator,
        filterParams: {
          valueFormatter: ({ value }: any) => {
            return value ? "Materials Linked" : "No Materials Linked";
          },
          keyCreator: ({ value }: any) => {
            return value ? "Materials Linked" : "No Materials Linked";
          }
        }
      }),
      ...(skipAuxiliaryQueries
        ? []
        : [
            {
              colId: "action",
              field: "submittal_schedule_links",
              headerName: "Actions",
              headerTooltip: "Actions",
              pinned: "right" as const,
              initialWidth: 100,
              filter: false,
              sortable: false,
              menuTabs: [] as string[],
              cellRenderer: ActionsCellRenderer,
              suppressColumnsToolPanel: true,
              getQuickFilterText: () => "",
              suppressHeaderMenuButton: true,
              suppressHeaderContextMenu: true
            }
          ]),
      coldefSingleField({
        field: "risk_level",
        colId: "risk_level",
        headerName: submittalHeaderMap?.risk_level,
        initialWidth: 130,
        comparator: riskLevelComparator,
        filter: "agSetColumnFilter",
        cellRenderer: (params: ICellRendererParams) => {
          return riskCellRenderer(params, false);
        },
        cellClassRules: {
          highRisk: ({ value }) => {
            return value === RiskLevelType.High;
          },
          mediumRisk: ({ value }) => {
            return value === RiskLevelType.Medium;
          },
          lowRisk: ({ value }) => {
            return value === RiskLevelType.Low;
          }
        }
      }),

      coldefSingleField({
        field: "priority",
        colId: "priority",
        headerName: submittalHeaderMap?.priority || "",
        headerTooltip: submittalHeaderMap?.priority || "",
        tooltipField: undefined,
        editable: isGridCellEditableForBasicFields,
        initialWidth: 150,
        minWidth: 100,
        cellEditor: SelectCellEditor,
        cellEditorParams: {
          type: "regular-select",
          dataMapVar: "priorities",
          valueVar: "value",
          labelVar: "label",
          showSearch: false,
          isSorting: false
        },
        cellClass: (params: any) => {
          return isGridCellEditableForBasicFields(params)
            ? "cell-editable"
            : "";
        }
      }),
      coldefSingleField({
        field: "watchers",
        colId: "watchers",
        headerName: submittalHeaderMap?.watchers,
        initialWidth: 130,
        editable: isGridCellEditableForBasicFields,
        cellClass: (params: any) => {
          return isGridCellEditableForBasicFields(params)
            ? "cell-editable"
            : "";
        }
      }),
      coldefDateField({
        field: "last_manual_updated_at",
        colId: "last_manual_updated_at",
        headerName:
          submittalHeaderMap?.last_manual_updated_at || "Last Updated by User",
        headerTooltip:
          submittalHeaderMap?.last_manual_updated_at || "Last Updated by User",
        ...dateTimeAuditColumnOverrides("last_manual_updated_at")
      }),
      coldefDateField({
        field: "last_system_updated_at",
        colId: "last_system_updated_at",
        headerName:
          submittalHeaderMap?.last_system_updated_at ||
          "Last Updated by System",
        headerTooltip:
          submittalHeaderMap?.last_system_updated_at ||
          "Last Updated by System",
        ...dateTimeAuditColumnOverrides("last_system_updated_at")
      }),
      ...(isIntegrationMode && !isProcoreProject
        ? [
            coldefSingleField({
              field: "not_found_in_source",
              colId: "not_found_in_source",
              headerName:
                submittalHeaderMap?.not_found_in_source || "Missing In Sync",
              headerTooltip:
                submittalHeaderMap?.not_found_in_source || "Missing In Sync",
              cellRenderer: missingInSyncCellRenderer,
              comparator: notFoundInSourceComparator,
              filterParams: {
                valueFormatter: ({ value }: any) => {
                  return value ? "Missing" : "Present";
                },
                keyCreator: ({ value }: any) => {
                  return value ? "Missing" : "Present";
                }
              }
            })
          ]
        : []),
      ...dateBlockColumnsDefs.groupCols,
      ...dateBlockColumnsDefs.columnDefsForDateBlock,
      ...coldefCustomFields,
      {
        colId: "planned_submittal_distributed_date",
        field: "planned_submittal_distributed_date",
        headerName: "Submittal Distributed Date (Planned)",
        headerTooltip: "Submittal Distributed Date (Planned)",
        hide: true,
        suppressColumnsToolPanel: true,
        initialWidth: 150,
        minWidth: 70,
        maxWidth: 250,
        editable: false,
        cellRenderer: dateCellRenderer,
        comparator: DateFilter.comparator,
        filter: CustomDateSetFilter,
        getQuickFilterText: () => ""
      },
      {
        colId: "planned_trade_partner_submitted_date",
        field: "planned_trade_partner_submitted_date",
        headerName: "Submitted By SUBCONTRACTOR (Planned)",
        headerTooltip: "Submitted By SUBCONTRACTOR (Planned)",
        initialWidth: 150,
        minWidth: 70,
        maxWidth: 350,
        editable: false,
        hide: true,
        suppressColumnsToolPanel: true,
        cellRenderer: dateCellRenderer,
        comparator: DateFilter.comparator,
        filter: CustomDateSetFilter,
        getQuickFilterText: () => ""
      },
      {
        colId: "planned_ext_review_completed_date",
        field: "planned_ext_review_completed_date",
        headerName: "External Review Complete (Planned)",
        headerTooltip: "External Review Complete (Planned)",
        initialWidth: 150,
        minWidth: 70,
        maxWidth: 300,
        editable: false,
        hide: true,
        suppressColumnsToolPanel: true,
        cellRenderer: dateCellRenderer,
        comparator: DateFilter.comparator,
        filter: CustomDateSetFilter,
        getQuickFilterText: () => ""
      },
      {
        colId: "submittal_created_by",
        headerName: "Created By",
        headerTooltip: "Created By",
        hide: true,
        suppressColumnsToolPanel: true,
        initialWidth: 150,
        minWidth: 70,
        maxWidth: 300,
        editable: false,
        valueGetter: ({ data }: any) => {
          try {
            return `${data.created_by_first_name} ${data.created_by_last_name}`;
          } catch {
            return "";
          }
        },
        getQuickFilterText: () => ""
      },
      {
        colId: "created_at",
        field: "created_at",
        headerName: submittalHeaderMap?.created_at || "",
        headerTooltip: submittalHeaderMap?.created_at || "",
        initialWidth: 200,
        minWidth: 30,
        maxWidth: 220,
        editable: false,
        cellEditorPopup: false,
        comparator: DateFilter.comparator,
        filter: CustomDateSetFilter,
        getQuickFilterText: () => "",
        tooltipValueGetter: (params: any) => {
          return DateUtils.format(params.value);
        },
        tooltipField: "created_at",
        suppressColumnsToolPanel: true,
        hide: true
      }
    ] as (ColDef<any> | ColGroupDef<any>)[];
  }, [
    dateBlockColumnsDefs.columnDefsForDateBlock,
    dateBlockColumnsDefs.groupCols,
    isGridCellEditableForBasicFields,
    isGridCellEditableForWorkflowFields,
    isGridCellEditableForWorkflowFieldsNotClosed,
    projectDetails?.spec_section,
    projectId,
    skipAuxiliaryQueries,
    submittalHeaderMap,
    coldefCustomFields,
    isIntegrationMode,
    isProcoreProject
  ]);

  const applyColumnConfig = useCallback(
    ({
      type,
      columnConfig
    }: {
      type: "default" | "custom";
      columnConfig?: any;
    }) => {
      if (!gridRef.current) return "";

      const colConfig =
        type === "default"
          ? resolveEffectiveColumnConfig(
              columnConfigData?.project_users?.[0]?.submittal_column_config,
              masterColumnConfigData?.master_column_config,
              FeatureTypes.SUBMITTALS
            )
          : columnConfig;

      const orderedDateBlockColIds = getInterleavedDateBlockColumnIds(
        // dateBlockColumnsDefs is always a defined object whose columnDefsForDateBlock is always an array (see useMemo above), so the `?.` and `|| []` fallbacks are unreachable
        /* v8 ignore next */
        dateBlockColumnsDefs?.columnDefsForDateBlock || []
      );

      if (colConfig?.columnList?.length) {
        let sortedColumnConfig = [...colConfig.columnList].sort(
          (a: any, b: any) => a.order - b.order
        );

        if (orderedDateBlockColIds.length > 0) {
          const milestonesColumnVisibility =
            sortedColumnConfig.find(
              (col: any) => col.field.name === "_milestones"
            )?.visibility ?? false;

          const durationsColumnVisibility =
            sortedColumnConfig.find(
              (col: any) => col.field.name === "_durations"
            )?.visibility ?? false;

          const baseConfig = sortedColumnConfig.filter((col: any) => {
            const name = col?.field?.name;
            if (name === "_milestones" || name === "_durations") return false;
            if (isMilestoneOrDurationColumnId(name)) return false;
            return true;
          });

          const existingLeaf = sortedColumnConfig.filter((col: any) =>
            isMilestoneOrDurationColumnId(col?.field?.name)
          );

          const maxOrder =
            sortedColumnConfig.reduce((acc: number, col: any) => {
              const order = typeof col?.order === "number" ? col.order : 0;
              return Math.max(acc, order);
            }, 0) || 0;

          const leafConfig = generateSavedColConfigsForColIds(
            orderedDateBlockColIds,
            maxOrder,
            {
              milestonesColumnVisibility,
              durationsColumnVisibility
            },
            existingLeaf
          );

          sortedColumnConfig = [...baseConfig, ...leafConfig];
        }

        sortedColumnConfig =
          backwardCompatibilityForLinkedTasksFilter(sortedColumnConfig);

        UtilColumnConfig.setFilterStateFromDataForSubmittalLog({
          columnStates: {
            columnList: sortedColumnConfig,
            quickSearch: colConfig?.quickSearch ?? "",
            conditionals_formatting: colConfig?.conditionals_formatting ?? []
          },
          gridRef: {
            api: gridRef.current!.api
          }
        });
        onConditionalsFormattingChange?.(
          colConfig?.conditionals_formatting ?? []
        );
        return colConfig?.quickSearch ?? "";
      }

      if (
        type === "default" &&
        (userColumnConfigLoading || masterColumnConfigLoading)
      ) {
        return "";
      }

      gridRef.current!.api.applyColumnState({
        state: [{ colId: "next_planned_deadline", sort: "asc", sortIndex: 1 }],
        defaultState: { sort: null }
      });
      return "";
    },
    [
      columnConfigData?.project_users,
      masterColumnConfigData?.master_column_config,
      dateBlockColumnsDefs?.columnDefsForDateBlock,
      gridRef,
      onConditionalsFormattingChange,
      userColumnConfigLoading,
      masterColumnConfigLoading
    ]
  );

  const setColumnVisibility = useCallback(
    (column: string[], visible: boolean) => {
      gridRef?.current?.api?.setColumnsVisible(column, visible);
    },
    [gridRef]
  );

  const saveColumnState = useCallback(async () => {
    if (skipAuxiliaryQueries || isProjectArchived || !gridRef.current) return;
    const columnsStates = UtilColumnConfig.getColumnStateFromGrid({
      gridRef: {
        api: gridRef.current!.api
      },
      conditionals_formatting: getConditionalsFormatting?.() ?? []
    });

    try {
      const variables = {
        submittal_column_config: columnsStates
      };

      const updateResponse = await saveColumnsStateMutation({
        variables
      });
      if (updateResponse?.success !== false) {
        await refetchUserColumnConfig();
      }
    } catch {
      console.error("An error occured");
    }
  }, [
    getConditionalsFormatting,
    gridRef,
    refetchUserColumnConfig,
    saveColumnsStateMutation,
    skipAuxiliaryQueries,
    isProjectArchived
  ]);

  const saveColumnStateRef = useRef(saveColumnState);
  useEffect(() => {
    saveColumnStateRef.current = saveColumnState;
  }, [saveColumnState]);
  const debouncedSaveColumnState = useMemo(
    () =>
      debounce(() => {
        saveColumnStateRef.current();
      }, 1000),
    []
  );

  return {
    gridDefaultColDef,
    gridColumnDefs,
    dateBlockColumnsDefs,
    setColumnVisibility,
    saveColumnState: debouncedSaveColumnState,
    applyColumnConfig,
    isColumnConfigReady,
    onEditCell
  };
}

export default useSubmittalLogGridConfig;
